import { Column, Line } from '@ant-design/plots';
import { useEffect, useState, useRef } from 'react';
import { Flex, Segmented, ConfigProvider, Spin, Divider  } from 'antd';
import { StatisticCard } from '@ant-design/pro-components';
import dayjs from "dayjs";

export const CostChart = ({data, granularity="Monthly", isLoading}) => {
  const [chartdata, setChartdata] = useState([]);
  const [totalCost, setTotalCost] = useState("");
  const [averageCost, setAverageCost] = useState("");
  const [groupby, setGroupby] = useState("Service count");
  const [groupbyCount, setGroupbyCount] = useState(0);
  const date2Str = (date) => {
    return granularity=="Monthly"?dayjs(date, 'YYYY-MM-DD').format('MMM YYYY'):dayjs(date, 'YYYY-MM-DD').format('MMM-DD');
  }
  useEffect(() => {
    if(data){
      setTotalCost("$" + data.TotalCost.toFixed(2));
      setGroupby(data.Title);
      setGroupbyCount(data.Fields.length);
      let cd = [];
      let fields = data.Fields;
      let subTotals = data.TotalCol;
      let sortedFields = [];
      if(fields.length> 10){        
        for(let i=0; i<fields.length; i++){
          sortedFields.push({f: fields[i], v: subTotals[i], idx: i});
        }
        sortedFields.sort((a, b) => b.v - a.v);
      }
      let dates = data.Dates;
      setAverageCost("$" + (data.TotalCost/dates.length).toFixed(2));
      dates.forEach(element => {
        let values = element.Values;
        if(values.length > 0){
          if(values.length > 10){
             let acc = 0;
             for(let i=0; i<9; i++){
               cd.push({
                date: date2Str(element.Date),
                value: parseFloat(values[sortedFields[i].idx].toFixed(2)),
                name: sortedFields[i].f,
                total: parseFloat(element.Total).toFixed(2)
              });
              acc += values[sortedFields[i].idx];
             }
             cd.push({
              date: date2Str(element.Date),
              value: parseFloat((element.Total - acc).toFixed(2)),
              name: "Others",
              total: parseFloat(element.Total).toFixed(2)
            });
          } else {
            let idx = 0;
            values.forEach(value => {
              cd.push({
                date: date2Str(element.Date),
                value: parseFloat(value.toFixed(2)),
                name: fields[idx++],
                total: parseFloat(element.Total).toFixed(2)
              });
            });
          }
        } else {
          cd.push({
            date: date2Str(element.Date),
            value: parseFloat(element.Total.toFixed(2)),
            name: 'Cost'
          });
        }
      });
      setChartdata(cd);      
      
    }
  }, [data])
  const { Divider } = StatisticCard;
  const [selectedChart, setSelectedChart] = useState('stackBar');

  const hoveredItem = useRef(null);
  const barTooltip = {
    elementHighlight:true,
    tooltip: {
      shared: true,
      marker: false,
      render: (e, { title, items }) => {
        items.sort((a, b) => b.value - a.value);
        return (
          <div>
            <h4><b>{title}</b></h4>
            {items.map((item) => {
              const { name, color, value} = item;
              let isHovered = hoveredItem.current === name;
              return (
                <div>
                  <div style={{ margin: 0, display: 'flex', justifyContent: 'space-between', backgroundColor: isHovered?'#f5f5f5':'#fff'}}>
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: color,
                          marginRight: 6,
                        }}
                      ></span>
                      <span style={{fontWeight:isHovered?'bold':'normal'}}>
                        {name}
                      </span>
                    </div>
                    <span style={{paddingLeft:"8px", fontWeight:isHovered?'bold':'normal'}}>{"$" + value.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
            {items.length > 1 && <div><hr color='#eee'/>
            <div style={{ margin: 0, display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <span>
                      <b>Total costs</b>
                      </span>
                    </div>
                    <span style={{paddingLeft:"8px"}}><b>${items[0].total}</b></span>
                  </div>
            </div>}
          </div>
        );
      },
    },
  };

  const _onReady = (chart) => {
    /*chart.on('*', (ev) => {
      if(ev.data && ev.data){
        console.log(ev.type);
        console.log(ev.data);
      }
    });*/
    chart.on('pointerover', (ev) => {
      if(ev.data && ev.data.data){
        hoveredItem.current = ev.data.data.name;
      }
    });
    chart.on('pointerout', (ev) => {
      if(ev.data && ev.data.data){
        hoveredItem.current = "";
      }
    });
  };
  return (
    <Flex gap="small" align="flex-start" vertical>
 
        <StatisticCard.Group direction='row'>
          <StatisticCard
            statistic={{
              title: 'Total cost',
              value: totalCost,
            }}
          />
          <Divider type='vertical'/>
          <StatisticCard
            statistic={{
              title: `Average ${granularity.toLowerCase()} cost`,
              value: averageCost,
            }}
          /> 
          {groupbyCount > 0 && <Divider type='vertical'/>}
          {groupbyCount > 0 && <StatisticCard
            statistic={{
              title: `${groupby} count`,
              value: groupbyCount,
            }}/>}        
        </StatisticCard.Group>

        <Flex style={{width: '100%'}} justify='space-between' align='center'>
          <span style={{fontWeight:'bold'}}>Costs($)</span>
          <ConfigProvider theme={{
              components: {
                Segmented: {
                  itemActiveBg: '#fff',
                  itemColor: '#545b64',
                  itemSelectedBg: '#006ce0',
                  itemSelectedColor: '#fff',
                  itemHoverBg:'#fff',
                  itemHoverColor:'#0073bb',
                },
              },
            }}>
            <Segmented
              size='middle'
              shape="round"
              options={[
                { value: 'bar', icon: <BarChartIcon /> },
                { value: 'line', icon: <LineChartIcon /> },
                { value: 'stackBar', icon: <GradientBarIcon /> },
              ]}
              defaultValue='stackBar'
              onChange={(value) => {
                setSelectedChart(value);
              }}
            />
          </ConfigProvider>
      </Flex>
      <div style={{width: '100%'}}>
        {isLoading && <Spin size='large' style={{position: 'absolute', top: '50%', left: '50%'}}/>}
        {!isLoading && selectedChart == "stackBar" && <Column
          data={chartdata}
          autoFit={true}
          xField="date"
          yField="value"
          colorField= 'name'
          stack={true}
          style={{
            maxWidth: 100,
          }}
          axis={{
            x: { style: {
              labelTransform: 'rotate(0)',
            } },
            y: { labelFormatter: '~s' },
          }}
          legend={ {
            color: {
              position: "bottom"
            },
          }}
          label={{
            position: 'middle',
            style: {}
          }}
          tooltip={(item) => {
            return { value: item.value, total:item.total };
          }}
          interaction={barTooltip}
          onReady={_onReady}
        />}
        {!isLoading && selectedChart == "line" && <Line
          data={chartdata}
          xField="date"
          yField="value"
          colorField= 'name'
          axis={{
            y: { labelFormatter: '~s' },
          }}
          legend= {{ size: false, color: {
            position: "bottom"
          } }}
          tooltip={(item) => {
            return { value: item.value, total:item.total };
          }}
          interaction={barTooltip}
          onReady={(chart) => {
            chart.on('pointerover', (ev) => {
              if(ev.data && ev.data.data && ev.data.data.length > 0){
                hoveredItem.current = ev.data.data[0].name;
              }
            });
            chart.on('pointerout', (ev) => {
              if(ev.data && ev.data.data && ev.data.data.length > 0){
                hoveredItem.current = "";
              }
            });
          }}
        />}
        {!isLoading && selectedChart == "bar" && <Column
            data={chartdata}
            xField="date"
            yField="value"
            colorField= 'name'
            group={true}
            axis={{
              y: { labelFormatter: '~s' },
            }}
            legend={ {
              color: {
                position: "bottom"
              },
            }}
            label={{
              position: 'middle',
              style: {}
            }}
            tooltip={(item) => {
              return { value: item.value, total:item.total };
            }}
            interaction={barTooltip}
            onReady={_onReady}
          />}
    </div>
    </Flex>
  )
};


const LineChartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    style={{ verticalAlign: 'middle' }} 
  >
    <g fillRule="evenodd" transform="translate(0 2)">
      <ellipse cx="1.5" cy="9.112" fill="currentColor" rx="1.5" ry="1.439" />
      <ellipse cx="9.5" cy="7.193" fill="currentColor" rx="1.5" ry="1.439" />
      <path
        stroke="currentColor"
        strokeLinecap="square"
        d="m9.5 7.193 4-5.754"
      />
      <ellipse cx="5.5" cy="3.357" fill="currentColor" rx="1.5" ry="1.439" />
      <ellipse cx="13.5" cy="1.439" fill="currentColor" rx="1.5" ry="1.439" />
      <path
        stroke="currentColor"
        strokeLinecap="square"
        d="m1.5 9.112 4-5.755M5.5 3.357l4 3.836"
      />
    </g>
  </svg>
);

const BarChartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    style={{ verticalAlign: 'middle' }} 
  >
    <g fill="currentColor" fillRule="evenodd">
      <path d="M0 0h3v15H0zM4 4h3v11H4zM8 8h3v7H8zM12 10h3v5h-3z" />
    </g>
  </svg>
);

const GradientBarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    style={{ verticalAlign: 'middle', fill: 'none' }}
  >
    <g fillRule="evenodd">
      <path stroke="currentColor" d="M0 0h3v9.5H0z" />
      <path fill="currentColor" d="M0 9.5h3V15H0z" />

      <path stroke="currentColor" d="M4 4h3v4H4z" />
      <path fill="currentColor" d="M4 8h3v7H4z" />

      <path stroke="currentColor" d="M8 8h3v2.5H8z" />
      <path fill="currentColor" d="M8 10.5h3V15H8z" />

      <path stroke="currentColor" d="M12 10h3v3h-3z" />
      <path fill="currentColor" d="M12 13h3v2h-3z" />
    </g>
  </svg>
);


